import { PublicKeyCredentialWithAssertionJSON } from '@github/webauthn-json';
import { get, parseRequestOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import { PublicKeyCredentialRequestOptionsJSON } from '@github/webauthn-json/dist/types/basic/json';

export enum WebAuthnRegistrationErrorCode {
  WebAuthnNotSupported = 'webauthn_not_supported',
  InvalidCreationOptions = 'invalid_creation_options',
  Aborted = 'aborted',
  DiscoverableCredentialSupportRequired = 'discoverable_credential_support_required',
  UserVerificationSupportRequired = 'user_verification_support_required',
  PreviouslyRegistered = 'previously_registered',
  NotAllowed = 'not_allowed',
  MalformedPubKeyCredParams = 'malformed_pub_key_cred_params',
  NoSupportedPubKeyCredParamsAlgorithm = 'no_supported_pub_key_cred_params_algorithm',
  InvalidDomain = 'invalid_domain',
  InvalidRpId = 'invalid_rp_id',
  InvalidUserIdLength = 'invalid_user_id_length',
  UnknownError = 'unknown_error',
}

export enum WebAuthnAuthenticationErrorCode {
  WebAuthnNotSupported = 'webauthn_not_supported',
  InvalidRequestOptions = 'invalid_request_options',
  Aborted = 'aborted',
  NotAllowed = 'not_allowed',
  InvalidDomain = 'invalid_domain',
  InvalidRpId = 'invalid_rp_id',
  UnknownError = 'unknown_error',
}

type TranslateRegistrationErrorProps = {
  error: Error;
  options: CredentialCreationOptions;
};

type TranslateAuthenticationErrorProps = {
  error: Error;
  options: CredentialRequestOptions;
};

export const isWebAuthnSupported = () => !!window.PublicKeyCredential;

/**
 * Source: https://github.com/MasterKale/SimpleWebAuthn/blob/master/packages/browser/src/helpers/isValidDomain.ts
 *
 * A simple test to determine if a hostname is a properly-formatted domain name
 *
 * Regex sourced from here:
 * https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch08s15.html
 *
 * This regex is checking if a given string is a valid domain name.
 * It should start with a series of alphanumeric characters (can be separated by hyphens), followed by one or more dots, and end with a top-level domain of at least two alphabetic characters.
 */
const isValidDomain = (hostname: string): boolean =>
  hostname === 'localhost' || /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(hostname);

// https://github.com/MasterKale/SimpleWebAuthn/blob/master/packages/browser/src/helpers/identifyRegistrationError.ts
export const translateRegistrationError = ({
  error,
  options,
}: TranslateRegistrationErrorProps): WebAuthnRegistrationErrorCode => {
  const { publicKey } = options;
  if (!publicKey) {
    return WebAuthnRegistrationErrorCode.InvalidCreationOptions;
  }
  switch (error.name) {
    case 'AbortError':
      if (options.signal instanceof AbortSignal) {
        // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 16)
        return WebAuthnRegistrationErrorCode.Aborted;
      }
      break;
    case 'ConstraintError':
      if (publicKey.authenticatorSelection?.requireResidentKey === true) {
        // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 4)
        return WebAuthnRegistrationErrorCode.DiscoverableCredentialSupportRequired;
      }
      if (publicKey.authenticatorSelection?.userVerification === 'required') {
        // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 5)
        return WebAuthnRegistrationErrorCode.UserVerificationSupportRequired;
      }
      break;
    case 'InvalidStateError':
      // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 20)
      // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 3)
      return WebAuthnRegistrationErrorCode.PreviouslyRegistered;
    case 'NotAllowedError':
      // Platforms are overloading this error beyond what the spec defines
      // https://stackoverflow.com/questions/77448336/handling-user-cancellation-when-creating-credentials-with-webauthn-api
      return WebAuthnRegistrationErrorCode.NotAllowed;
    case 'NotSupportedError':
      const hasValidPubKeyCredParams =
        publicKey.pubKeyCredParams.some(param => param.type === 'public-key') || false;
      if (!hasValidPubKeyCredParams) {
        // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 2)
        return WebAuthnRegistrationErrorCode.MalformedPubKeyCredParams;
      }
      // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 2)
      return WebAuthnRegistrationErrorCode.NoSupportedPubKeyCredParamsAlgorithm;
    case 'SecurityError':
      const effectiveDomain = window.location.hostname;
      if (!isValidDomain(effectiveDomain)) {
        // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 7)
        return WebAuthnRegistrationErrorCode.InvalidDomain;
      }
      if (publicKey.rp.id !== effectiveDomain) {
        // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 8)
        return WebAuthnRegistrationErrorCode.InvalidRpId;
      }
      break;
    case 'TypeError':
      if (publicKey.user.id.byteLength < 1 || publicKey.user.id.byteLength > 64) {
        // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 5)
        return WebAuthnRegistrationErrorCode.InvalidUserIdLength;
      }
      break;
    case 'UnknownError':
      // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 1)
      // https://www.w3.org/TR/webauthn-2/#sctn-op-make-cred (Step 8)
      return WebAuthnRegistrationErrorCode.UnknownError;
  }
  // for anything else, return unknown error
  return WebAuthnRegistrationErrorCode.UnknownError;
};

// https://github.com/MasterKale/SimpleWebAuthn/blob/master/packages/browser/src/helpers/identifyAuthenticationError.ts
export const translateAuthenticationError = ({
  error,
  options,
}: TranslateAuthenticationErrorProps): WebAuthnAuthenticationErrorCode => {
  const { publicKey } = options;
  if (!publicKey) {
    return WebAuthnAuthenticationErrorCode.InvalidRequestOptions;
  }
  switch (error.name) {
    case 'AbortError':
      if (options.signal instanceof AbortSignal) {
        // https://www.w3.org/TR/webauthn-2/#sctn-createCredential (Step 16)
        return WebAuthnAuthenticationErrorCode.Aborted;
      }
      break;
    case 'NotAllowedError':
      // Platforms are overloading this error beyond what the spec defines
      return WebAuthnAuthenticationErrorCode.NotAllowed;
    case 'SecurityError':
      const effectiveDomain = window.location.hostname;
      if (!isValidDomain(effectiveDomain)) {
        // https://www.w3.org/TR/webauthn-2/#sctn-discover-from-external-source (Step 5)
        return WebAuthnAuthenticationErrorCode.InvalidDomain;
      }
      if (publicKey.rpId !== effectiveDomain) {
        // https://www.w3.org/TR/webauthn-2/#sctn-discover-from-external-source (Step 6)
        return WebAuthnAuthenticationErrorCode.InvalidRpId;
      }
      break;
    case 'UnknownError':
      // https://www.w3.org/TR/webauthn-2/#sctn-op-get-assertion (Step 1)
      // https://www.w3.org/TR/webauthn-2/#sctn-op-get-assertion (Step 12)
      return WebAuthnAuthenticationErrorCode.UnknownError;
  }
  // for anything else, return unknown error
  return WebAuthnAuthenticationErrorCode.UnknownError;
};

type Result<T, E> = { success: true; data: T } | { success: false; error: E };

type AuthenticatorChalengeResult = Result<
  PublicKeyCredentialWithAssertionJSON,
  WebAuthnAuthenticationErrorCode
>;

export const challengeWithAuthenticator = async (
  requestOptions: PublicKeyCredentialRequestOptionsJSON
): Promise<AuthenticatorChalengeResult> => {
  try {
    const options = parseRequestOptionsFromJSON({
      publicKey: {
        ...requestOptions,
        timeout: 60000,
      },
    });

    try {
      const securityKeyCredential = await get(options);
      return { success: true, data: securityKeyCredential.toJSON() };
    } catch (error) {
      captureErrorMessage('Authentication failed', {
        webauthnError: { name: error.name, message: JSON.stringify(error.message) },
      });

      const errorCode = translateAuthenticationError({ error: error, options });
      return { success: false, error: errorCode };
    }
  } catch (error) {
    captureErrorMessage('Failed to parse authenticate options for webauthn', {
      webauthnError: { name: error.name, message: JSON.stringify(error.message) },
    });

    return { success: false, error: WebAuthnAuthenticationErrorCode.InvalidRequestOptions };
  }
};
